import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { NETWORKS } from "@/lib/ambassadors";
import { A, Eyebrow, btnDark } from "./ui";

const EMPTY = { first_name: "", last_name: "", email: "", handle: "", network: "Instagram", profile_url: "", other_links: "", motivation: "", accept_terms: false, accept_privacy: false, website: "" };
const input = "h-12 w-full rounded-xl border border-[#D9C8B3] bg-white px-4 text-base text-[#141013] outline-none transition-colors focus:border-[#7A5418]";
const lbl = "mb-1.5 block text-sm font-medium text-[#141013]";

function validate(f) {
  const e = {};
  if (!f.first_name.trim()) e.first_name = "Indique ton prénom.";
  if (!f.last_name.trim()) e.last_name = "Indique ton nom.";
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(f.email.trim())) e.email = "Adresse e-mail invalide.";
  if (!f.handle.trim()) e.handle = "Indique ton nom ou pseudonyme.";
  if (!/^https?:\/\/\S{3,}$/i.test(f.profile_url.trim())) e.profile_url = "Colle le lien complet de ton profil (https://…).";
  if (f.motivation.trim().length < 20) e.motivation = "Dis-nous en quelques phrases pourquoi (20 caractères minimum).";
  if (!f.accept_terms) e.accept_terms = "Merci de confirmer la lecture des conditions.";
  if (!f.accept_privacy) e.accept_privacy = "Merci d'accepter le traitement de tes données.";
  return e;
}

function Field({ id, label, error, children }) {
  return (
    <div>
      <label htmlFor={id} className={lbl}>{label}</label>
      {children}
      {error ? <p data-testid={`amb-error-${id}`} className="mt-1 text-sm text-[#A12C2C]">{error}</p> : null}
    </div>
  );
}

export function AmbForm() {
  const [f, setF] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [state, setState] = useState("idle");
  const [serverErr, setServerErr] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate(f);
    setErrors(e);
    if (Object.keys(e).length) return;
    setState("sending");
    setServerErr("");
    try {
      await api.post("/ambassadors/apply", f);
      setState("done");
    } catch (err) {
      setServerErr(errMsg(err));
      setState("idle");
    }
  };

  if (state === "done") {
    return (
      <div data-testid="amb-form-success" role="status" className="rounded-3xl border border-[#D9C8B3] bg-white p-10 text-center">
        <CheckCircle2 className="mx-auto h-10 w-10 text-[#7A5418]" />
        <p className={`mt-4 font-display text-2xl ${A.ink}`}>Merci pour ta candidature et ton intérêt pour Ozentya !</p>
        <p className={`mt-3 text-base ${A.muted}`}>Nous étudierons ton profil et te contacterons par e-mail.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate data-testid="amb-form" className="grid gap-5 rounded-3xl border border-[#D9C8B3] bg-[#FBF8F3] p-6 md:grid-cols-2 md:p-10">
      <Field id="first_name" label="Prénom" error={errors.first_name}><input id="first_name" autoComplete="given-name" value={f.first_name} onChange={set("first_name")} data-testid="amb-input-first-name" className={input} /></Field>
      <Field id="last_name" label="Nom" error={errors.last_name}><input id="last_name" autoComplete="family-name" value={f.last_name} onChange={set("last_name")} data-testid="amb-input-last-name" className={input} /></Field>
      <Field id="email" label="Adresse e-mail" error={errors.email}><input id="email" type="email" autoComplete="email" value={f.email} onChange={set("email")} data-testid="amb-input-email" className={input} /></Field>
      <Field id="handle" label="Nom ou pseudonyme sur les réseaux" error={errors.handle}><input id="handle" value={f.handle} onChange={set("handle")} data-testid="amb-input-handle" className={input} /></Field>
      <Field id="network" label="Réseau social principal"><select id="network" value={f.network} onChange={set("network")} data-testid="amb-input-network" className={input}>{NETWORKS.map((n) => <option key={n}>{n}</option>)}</select></Field>
      <Field id="profile_url" label="Lien vers ton profil principal" error={errors.profile_url}><input id="profile_url" type="url" placeholder="https://www.instagram.com/…" value={f.profile_url} onChange={set("profile_url")} data-testid="amb-input-profile-url" className={input} /></Field>
      <div className="md:col-span-2"><Field id="other_links" label="Liens vers tes autres profils (facultatif)"><input id="other_links" value={f.other_links} onChange={set("other_links")} data-testid="amb-input-other-links" className={input} /></Field></div>
      <div className="md:col-span-2"><Field id="motivation" label="Pourquoi souhaites-tu devenir ambassadeur Ozentya ?" error={errors.motivation}>
        <textarea id="motivation" rows={5} maxLength={2000} value={f.motivation} onChange={set("motivation")} data-testid="amb-input-motivation" className={`${input} h-auto py-3`} /></Field></div>
      <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" value={f.website} onChange={set("website")} className="hidden" />
      <div className="space-y-3 md:col-span-2">
        <label className={`flex items-start gap-3 text-sm ${A.ink}`}><input type="checkbox" checked={f.accept_terms} onChange={set("accept_terms")} data-testid="amb-input-terms" className="mt-0.5 h-5 w-5 accent-[#7A5418]" />
          <span>J'ai lu les <a href="#conditions" className="underline underline-offset-4">conditions du programme</a>.</span></label>
        {errors.accept_terms ? <p data-testid="amb-error-accept_terms" className="text-sm text-[#A12C2C]">{errors.accept_terms}</p> : null}
        <label className={`flex items-start gap-3 text-sm ${A.ink}`}><input type="checkbox" checked={f.accept_privacy} onChange={set("accept_privacy")} data-testid="amb-input-privacy" className="mt-0.5 h-5 w-5 accent-[#7A5418]" />
          <span>J'accepte qu'Ozentya (SASU K-RAPAT) utilise ces informations pour étudier ma candidature et me recontacter. Elles sont conservées 2 ans au plus et je peux demander leur suppression à tout moment (<Link to="/confidentialite" className="underline underline-offset-4">politique de confidentialité</Link>).</span></label>
        {errors.accept_privacy ? <p data-testid="amb-error-accept_privacy" className="text-sm text-[#A12C2C]">{errors.accept_privacy}</p> : null}
      </div>
      {serverErr ? <p data-testid="amb-form-server-error" role="alert" className="rounded-xl bg-[#F8E1E1] p-4 text-sm text-[#7A1F1F] md:col-span-2">{serverErr}</p> : null}
      <button type="submit" disabled={state === "sending"} data-testid="amb-form-submit" className={`${btnDark} disabled:opacity-60 md:col-span-2 md:w-fit`}>
        {state === "sending" ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Envoyer ma candidature
      </button>
    </form>
  );
}

export function AmbApply() {
  return (
    <section id="candidature" data-testid="amb-apply" className={`${A.bg} scroll-mt-24 px-5 py-20 md:px-10 md:py-28`}>
      <div className="mx-auto max-w-4xl">
        <Eyebrow>Candidature</Eyebrow>
        <h2 className={`font-display text-3xl md:text-4xl ${A.ink}`}>Deviens ambassadeur Ozentya</h2>
        <p className={`mt-4 mb-10 text-base ${A.muted}`}>Quelques minutes suffisent. Nous étudions chaque candidature avec attention.</p>
        <AmbForm />
      </div>
    </section>
  );
}
